import { type ReferralStage, referralStageLabel } from '@commissionsight/shared';
import { useCallback } from 'react';
import { Badge, ErrorNote, Metric, Panel, type Tone } from '../components/ui.js';
import { type AdminReferralRow, getReferralStats, listReferrals } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const usd = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const shortDate = (v: number | string | null) =>
  v
    ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const STAGE_TONE: Record<ReferralStage, Tone> = {
  signed_up: 'neutral',
  statements: 'sky',
  payment: 'amber',
  qualified: 'indigo',
  credited: 'emerald',
};

export function Referrals() {
  const stats = useAsyncData(useCallback(() => getReferralStats(), []));
  const list = useAsyncData(useCallback(() => listReferrals(200, 0), []));

  return (
    <div>
      <Panel title="Referral program">
        {stats.error && <ErrorNote message={stats.error} onRetry={stats.reload} />}
        {stats.data && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
            <Metric label="Total referrals" value={stats.data.total} tone="neutral" />
            <Metric label="2+ statements" value={stats.data.withStatements} tone="sky" />
            <Metric label="Payment added" value={stats.data.withPayment} tone="amber" />
            <Metric label="Qualified" value={stats.data.qualified} tone="indigo" />
            <Metric
              label="Flagged (abuse)"
              value={stats.data.flagged}
              tone={stats.data.flagged > 0 ? 'rose' : 'neutral'}
              hint="shared card / IP — not credited"
            />
            <Metric label="Credited" value={stats.data.credited} tone="emerald" />
            <Metric
              label="Credited this year"
              value={usd(stats.data.thisYearCreditedCents)}
              tone="emerald"
              hint={`${usd(stats.data.creditedCents)} all-time`}
            />
          </div>
        )}
      </Panel>

      <Panel title="All referrals">
        {list.error && <ErrorNote message={list.error} onRetry={list.reload} />}
        {list.data && list.data.data.length === 0 && (
          <p className="text-sm text-neutral-500">No referrals yet.</p>
        )}
        {list.data && list.data.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="py-1 font-medium">Referrer</th>
                <th className="font-medium">Referred agency</th>
                <th className="font-medium">Stage</th>
                <th className="font-medium">Statements</th>
                <th className="font-medium">Payment</th>
                <th className="font-medium">Credited</th>
                <th className="text-right font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {list.data.data.map((r: AdminReferralRow, i) => (
                <tr
                  key={`${r.referrer}-${r.referred}-${i}`}
                  className="border-t border-neutral-100"
                >
                  <td className="py-2">{r.referrer}</td>
                  <td>
                    {r.referred}
                    {r.referredStatus !== 'active' && (
                      <span className="ml-1 text-xs text-neutral-400">({r.referredStatus})</span>
                    )}
                  </td>
                  <td>
                    <Badge tone={STAGE_TONE[r.stage]}>{referralStageLabel(r.stage)}</Badge>
                    {r.flaggedReason && (
                      <span className="ml-1">
                        <Badge tone="rose">flagged: {r.flaggedReason}</Badge>
                      </span>
                    )}
                  </td>
                  <td className="text-neutral-500">{shortDate(r.statementsAt)}</td>
                  <td className="text-neutral-500">{shortDate(r.paymentAt)}</td>
                  <td className="text-neutral-500">
                    {r.creditedAt ? `${shortDate(r.creditedAt)} · ${usd(r.creditCents ?? 0)}` : '—'}
                  </td>
                  <td className="text-right text-neutral-500">{shortDate(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
